/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the 'code splitting' technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// =================================================================================================
// Custom Code: Auto-fill Product Title in Contact Forms
// =================================================================================================

/**
 * Automatically fills the "Product title" field in contact forms with the current product title
 * when on a product page. This works without modifying templates or locales folders.
 */
(function() {
  'use strict';

  function isGermanLocale() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    return lang === 'de' || lang.startsWith('de-');
  }

  function getAutoFieldTitle(fieldType) {
    const german = isGermanLocale();

    if (fieldType === 'sku') {
      return german
        ? 'Dieser Wert wird automatisch anhand der aktuellen Produkt-SKU gesetzt'
        : 'This value is automatically set based on the current product SKU';
    }

    if (fieldType === 'reference') {
      return german
        ? 'Dieser Wert wird automatisch für diese Anfrage generiert'
        : 'This value is automatically generated for this enquiry';
    }

    return german
      ? 'Dieser Wert wird automatisch anhand des aktuellen Produkts gesetzt'
      : 'This value is automatically set based on the current product';
  }
  
  /**
   * Function to get the current product title from the page
   * @returns {string|null} The product title or null if not found
   */
  function getProductTitle() {
    // Method 1: Look for the specific Enterprise theme product title selector
    const productTitleElement = document.querySelector('.product-title');
    if (productTitleElement && productTitleElement.textContent.trim()) {
      return productTitleElement.textContent.trim();
    }
    
    // Method 2: Look for other common product title selectors
    const titleSelectors = [
      '.product__title',
      '[data-product-title]',
      'h1.product__title',
      'h1[data-product-title]',
      '.product-single__title',
      '.product-form__title',
      '.product-info__title h1',
      '.main-product h1',
      '[data-section-type="product"] h1'
    ];
    
    for (const selector of titleSelectors) {
      const element = document.querySelector(selector);
      if (element && element.textContent.trim()) {
        const title = element.textContent.trim();
        // Skip if it's just the company name
        if (title.toLowerCase() !== 'aldacom') {
          return title;
        }
      }
    }
    
    // Method 3: Look for JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of jsonLdScripts) {
      try {
        const jsonData = JSON.parse(script.textContent);
        
        // Handle Product schema
        if (jsonData['@type'] === 'Product' && jsonData.name) {
          return jsonData.name;
        }
        
        // Handle arrays of structured data
        if (Array.isArray(jsonData)) {
          for (const item of jsonData) {
            if (item['@type'] === 'Product' && item.name) {
              return item.name;
            }
          }
        }
      } catch (e) {
        // Ignore JSON parsing errors
        continue;
      }
    }
    
    // Method 4: Look for meta tags (but avoid company name)
    const metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle && metaTitle.content && metaTitle.content.toLowerCase() !== 'aldacom') {
      return metaTitle.content;
    }
    
    // Method 5: Use document title as fallback (try to extract product name)
    const pageTitle = document.title;
    if (pageTitle) {
      // Try to remove common separators and site names
      const titleParts = pageTitle.split(/[–—\-|]/);
      if (titleParts.length > 1) {
        const productPart = titleParts[0].trim();
        if (productPart.toLowerCase() !== 'aldacom') {
          return productPart;
        }
      }
    }
    
    return null;
  }
  
  /**
   * Function to check if we're on a product page
   * @returns {boolean} True if on a product page
   */
  function isProductPage() {
    // Check various indicators that we're on a product page
    return (
      document.body.classList.contains('template-product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-form') ||
      document.querySelector('.product__title') ||
      window.location.pathname.includes('/products/') ||
      document.querySelector('script[type="application/ld+json"]')?.textContent?.includes('"@type":"Product"')
    );
  }
  
  /**
   * Find current variant ID on the page
   * @returns {string|null}
   */
  function getCurrentVariantId() {
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], form[action*="/cart/add"] input[name="id"]');
    return idInput?.value || null;
  }

  /**
   * Try to read the SKU from the DOM (no network)
   * @returns {string|null}
   */
  function getProductSKUFromDOM() {
    const skuEl = document.querySelector('.product-sku__value, [data-product-sku]');
    const text = skuEl?.textContent?.trim();
    if (text) return text;

    // Try JSON-LD blocks
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        const asArray = Array.isArray(data) ? data : [data];
        for (const item of asArray) {
          if (item['@type'] === 'Product') {
            if (item.sku) return String(item.sku);
            // Some themes nest sku inside offers
            if (item.offers) {
              const offers = Array.isArray(item.offers) ? item.offers : [item.offers];
              for (const offer of offers) {
                if (offer.sku) return String(offer.sku);
              }
            }
          }
        }
      } catch (e) {
        // ignore
      }
    }
    return null;
  }

  /**
   * Fetch SKU via Shopify variant JSON endpoint
   * @param {string} variantId
   * @returns {Promise<string|null>}
   */
  function fetchSKUByVariantId(variantId) {
    if (!variantId) return Promise.resolve(null);
    return fetch(`/variants/${variantId}.json`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(json => json?.variant?.sku || null)
      .catch(() => null);
  }

  /**
   * Resolve current product SKU using DOM first, then network
   * @returns {Promise<string|null>}
   */
  function getProductSKU() {
    const fromDom = getProductSKUFromDOM();
    if (fromDom) return Promise.resolve(fromDom);
    const variantId = getCurrentVariantId();
    return fetchSKUByVariantId(variantId);
  }

  /**
   * Fill any contact-form fields that represent SKU and lock them
   * @param {string} sku
   */
  function fillSKUFields(sku, root = document) {
    if (!sku) return;
    const fieldSelectors = [
      'input[name*="sku" i]',
      'textarea[name*="sku" i]',
      // German and common localized handles
      'input[name*="artikelnummer" i]',
      'textarea[name*="artikelnummer" i]',
      'input[name*="artikelnr" i]',
      'textarea[name*="artikelnr" i]',
      'input[name*="teilenummer" i]',
      'textarea[name*="teilenummer" i]',
      'input[name*="teilenr" i]',
      'textarea[name*="teilenr" i]',
      // Dutch
      'input[name*="productnummer" i]',
      'textarea[name*="productnummer" i]'
    ];

    // Also look for labels that mention SKU (works across languages, SKU is universal)
    const labeledFields = [];
    const labels = root.querySelectorAll('label');
    labels.forEach(label => {
      const raw = label.textContent || '';
      const labelText = raw.toLowerCase().replace(/[:*]/g, '').trim();
      const normalized = labelText.replace(/\s+/g, ' ');
      const matches = [
        // Universal
        'sku',
        // German
        'artikelnummer', 'artikel nr', 'artikelnr', 'teile-nr', 'teilenr', 'teilenummer',
        // English
        'part number', 'product code',
        // Dutch
        'productnummer', 'artikelnummer',
        // French/Spanish/Italian/Portuguese common terms
        'référence', 'referencia', 'numero articolo', 'codice articolo', 'código do produto', 'codigo do produto'
      ];
      const keywordHit = matches.some(m => normalized.includes(m))
        || (normalized.includes('artikel') && normalized.includes('nummer'))
        || (normalized.includes('teil') && (normalized.includes('nr') || normalized.includes('nummer')));
      if (keywordHit) {
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          const el = root.getElementById ? root.getElementById(forAttr) : document.getElementById(forAttr);
          if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
            labeledFields.push(el);
          }
        }
      }
    });

    const allFields = [
      ...root.querySelectorAll(fieldSelectors.join(', ')),
      ...labeledFields
    ];

    let processedCount = 0;
    allFields.forEach(field => {
      if (!field) return;
      // Only set once
      if (!field.value || !field.value.trim()) {
        field.value = sku;
      }
      // Ensure submission via a hidden field with the same name
      if (field.getAttribute('name')) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = field.name; // preserve original name for localized emails
        hidden.value = sku;
        hidden.classList.add('product-sku-backup');
        field.parentNode.insertBefore(hidden, field);
        field.removeAttribute('name');
      }
      // Lock editing and add small visual cue
      field.readOnly = true;
      field.style.backgroundColor = '#f8f9fa';
      field.style.cursor = 'default';
      field.title = getAutoFieldTitle('sku');
      field.classList.add('auto-filled-product-sku');

      field.dispatchEvent(new Event('change', { bubbles: true }));
      field.dispatchEvent(new Event('input', { bubbles: true }));
      processedCount += 1;
    });

    // If a visible SKU field exists, DO NOT add the neutral fallback to avoid duplicates.
    // Only add the fallback if no visible SKU field is present.
    const contactForms = root.querySelectorAll ? root.querySelectorAll('form[action*="contact"]') : document.querySelectorAll('form[action*="contact"]');
    contactForms.forEach(form => {
      const neutral = form.querySelector('input[name="contact[product_sku]"]');
      if (processedCount > 0) {
        if (neutral) neutral.remove();
      } else {
        if (!neutral) {
          const hiddenSku = document.createElement('input');
          hiddenSku.type = 'hidden';
          hiddenSku.name = 'contact[product_sku]';
          hiddenSku.value = sku;
          form.appendChild(hiddenSku);
        } else {
          neutral.value = sku;
        }
      }
    });
  }

  /**
   * Main runner: populate SKU into contact forms on product pages
   */
  function autoFillSKU() {
    if (!isProductPage()) return;

    getProductSKU().then((sku) => {
      if (!sku) return;
      // Initial pass
      fillSKUFields(sku);

      // Update on variant changes (SKU can change per variant)
      document.addEventListener('on:variant:change', (e) => {
        const newVariant = e?.detail?.variant;
        if (newVariant && typeof newVariant.sku !== 'undefined') {
          const newSku = newVariant.sku || '';
          if (newSku) fillSKUFields(newSku);
        }
      });

      // Also fill when the contact details element is opened by the user
      const detailsEl = document.querySelector('.collapsible-contact-form');
      if (detailsEl) {
        detailsEl.addEventListener('toggle', () => {
          if (detailsEl.open) fillSKUFields(sku, detailsEl);
        });
      }
    });
  }

  /**
   * Fill any contact-form fields that represent Product Title and lock them
   * @param {string} productTitle
   * @param {HTMLElement|Document} root
   */
  function fillProductTitleFields(productTitle, root = document) {
    if (!productTitle) return;

    const fieldSelectors = [
      // English name attributes
      'input[name*="product_title" i]',
      'input[name*="product-title" i]',
      'input[name*="productname" i]',
      'input[name*="product_name" i]',
      'input[name*="product-name" i]',
      'input[name*="item_title" i]',
      'input[name*="item-title" i]',
      'input[name*="itemname" i]',
      'input[name*="item-name" i]',
      // German
      'input[name*="produktname" i]',
      'input[name*="produkttitel" i]',
      // Textareas
      'textarea[name*="product_title" i]',
      'textarea[name*="product-title" i]',
      'textarea[name*="productname" i]',
      'textarea[name*="product_name" i]',
      'textarea[name*="product-name" i]',
      'textarea[name*="item_title" i]',
      'textarea[name*="item-title" i]',
      'textarea[name*="itemname" i]',
      'textarea[name*="item-name" i]',
      'textarea[name*="produktname" i]',
      'textarea[name*="produkttitel" i]'
    ];

    const labeledFields = [];
    const labels = root.querySelectorAll ? root.querySelectorAll('label') : document.querySelectorAll('label');
    labels.forEach(label => {
      const labelText = (label.textContent || '').toLowerCase().replace(/[:*]/g, '').trim();
      const matches = [
        // English
        'product title', 'product-name', 'product name', 'producttitle',
        'item title', 'item-name', 'item name',
        // German
        'produktname', 'produkt titel', 'produkt-titel', 'produkttitel',
        // French
        'nom du produit', 'nom produit',
        // Spanish
        'nombre del producto', 'titulo del producto', 'título del producto',
        // Italian
        'nome del prodotto', 'titolo del prodotto',
        // Portuguese
        'nome do produto', 'titulo do produto', 'título do produto',
        // Dutch
        'productnaam', 'product titel', 'producttitel'
      ];
      if (matches.some(m => labelText.includes(m))) {
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          const el = root.getElementById ? root.getElementById(forAttr) : document.getElementById(forAttr);
          if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
            labeledFields.push(el);
          }
        }
      }
    });

    const allFields = Array.from(new Set([
      ...root.querySelectorAll ? root.querySelectorAll(fieldSelectors.join(', ')) : document.querySelectorAll(fieldSelectors.join(', ')),
      ...labeledFields
    ]));

    allFields.forEach(field => {
      if (!field) return;
      if (!field.value || !field.value.trim()) {
        field.value = productTitle;
      }
      
      const parent = field.parentNode;
      
      // Add/refresh hidden backup once and remove name from visible field
      if (field.getAttribute('name') && parent) {
        // Remove any previous backup to avoid duplicates
        const existing = parent.querySelector('input.product-title-backup');
        if (existing) existing.remove();
        
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = field.name; // preserve original name for localized emails
        hidden.value = productTitle;
        hidden.classList.add('product-title-backup');
        
        if (parent && typeof parent.insertBefore === 'function') {
          try {
            if (field.parentNode === parent) {
              parent.insertBefore(hidden, field);
            }
          } catch (e) {
            console.warn('Could not insert product title backup', e);
          }
        }
        
        field.removeAttribute('name');
      }

      field.readOnly = true;
      field.style.backgroundColor = '#f8f9fa';
      field.style.cursor = 'default';
      field.title = getAutoFieldTitle('product');
      field.classList.add('auto-filled-product-title');

      field.dispatchEvent(new Event('change', { bubbles: true }));
      field.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  /**
   * Main runner: populate Product Title into contact forms on product pages
   */
  function autoFillProductTitle() {
    if (!isProductPage()) return;

    const productTitle = getProductTitle();
    if (!productTitle) return;

    // Initial pass on the whole document
    fillProductTitleFields(productTitle);

    // Fill when the collapsible contact form is opened by the user
    const detailsEl = document.querySelector('.collapsible-contact-form');
    if (detailsEl) {
      detailsEl.addEventListener('toggle', () => {
        if (detailsEl.open) fillProductTitleFields(productTitle, detailsEl);
      });
    }

    // Observe DOM changes to catch dynamically inserted forms (e.g. apps/modals)
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          const el = node;
          if (el.matches && el.matches('form[action*="contact" i]')) {
            fillProductTitleFields(productTitle, el);
          } else if (el.querySelector) {
            const forms = el.querySelectorAll('form[action*="contact" i]');
            if (forms.length) forms.forEach(f => fillProductTitleFields(productTitle, f));
          }
        });
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Ensure the product title is always included on submit, even if no visible field matched
    function wireSubmitHandlers() {
      const forms = document.querySelectorAll('form[action*="contact" i]');
      forms.forEach(form => {
        if (form.dataset.productTitleSubmitWired === 'true') return;
        form.dataset.productTitleSubmitWired = 'true';
        form.addEventListener('submit', () => {
          const currentTitle = getProductTitle() || productTitle;
          if (!currentTitle) return;

          // Update existing backups if present
          const backups = form.querySelectorAll('input.product-title-backup');
          if (backups.length) {
            backups.forEach(b => { b.value = currentTitle; });
            return;
          }

          // Try to reuse an existing visible field's name if it looks like a product title field
          const visibleField = form.querySelector(
            'input[name*="product" i][name*="title" i],\
             textarea[name*="product" i][name*="title" i],\
             input[name*="produkt" i][name*="name" i],\
             textarea[name*="produkt" i][name*="name" i],\
             input[name*="produkttitel" i], textarea[name*="produkttitel" i]'
          );

          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = (visibleField && visibleField.name) ? visibleField.name : 'contact[product_title]';
          hidden.value = currentTitle;
          hidden.classList.add('product-title-backup');
          form.appendChild(hidden);
        });
      });
    }

    wireSubmitHandlers();
    // Re-wire handlers if new forms appear
    setTimeout(wireSubmitHandlers, 500);
  }

  /**
   * Generate a reference number for product enquiries.
   * Format: QYYMMDDXXXX
   * @returns {string}
   */
  function generateReferenceNumber() {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let suffix = '';

    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const randomValues = new Uint8Array(4);
      window.crypto.getRandomValues(randomValues);
      suffix = Array.from(randomValues, (value) => chars[value % chars.length]).join('');
    } else {
      for (let i = 0; i < 4; i += 1) {
        suffix += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    return `Q${yy}${mm}${dd}${suffix}`;
  }

  /**
   * Apply the generated reference number to any matching fields.
   * @param {string} referenceNumber
   * @param {HTMLElement|Document} root
   */
  function fillReferenceNumberFields(referenceNumber, root = document) {
    const fields = root.querySelectorAll
      ? root.querySelectorAll('[data-reference-number-field]')
      : document.querySelectorAll('[data-reference-number-field]');

    fields.forEach((field) => {
      if (!field.value) field.value = referenceNumber;
      field.readOnly = true;
      field.style.backgroundColor = '#f8f9fa';
      field.style.cursor = 'default';
      field.title = getAutoFieldTitle('reference');
      field.classList.add('auto-filled-reference-number');
      field.dispatchEvent(new Event('change', { bubbles: true }));
      field.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  /**
   * Main runner: populate Reference number into product contact forms.
   */
  function autoFillReferenceNumber() {
    if (!isProductPage()) return;

    const referenceNumber = generateReferenceNumber();
    fillReferenceNumberFields(referenceNumber);

    const detailsEl = document.querySelector('.collapsible-contact-form');
    if (detailsEl) {
      detailsEl.addEventListener('toggle', () => {
        if (detailsEl.open) fillReferenceNumberFields(referenceNumber, detailsEl);
      });
    }

    const forms = document.querySelectorAll('form[action*="contact" i]');
    forms.forEach((form) => {
      if (form.dataset.referenceNumberSubmitWired === 'true') return;
      form.dataset.referenceNumberSubmitWired = 'true';

      form.addEventListener('submit', () => {
        let field = form.querySelector('[data-reference-number-field]');

        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'contact[Reference number]';
          field.setAttribute('data-reference-number-field', '');
          form.appendChild(field);
        }

        if (!field.value) field.value = referenceNumber;
      });
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { autoFillSKU(); autoFillProductTitle(); autoFillReferenceNumber(); });
  } else {
    autoFillSKU();
    autoFillProductTitle();
    autoFillReferenceNumber();
  }

  // Also run after a short delay to catch any dynamically loaded content
  setTimeout(autoFillSKU, 1000);
  setTimeout(autoFillProductTitle, 1000);
  setTimeout(autoFillReferenceNumber, 1000);
  
})();

/**
 * Function to scroll to contact form and open it
 * Called from the product page "Contact us" button
 */
function scrollToContactForm() {
  function isGermanLocale() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    return lang === 'de' || lang.startsWith('de-');
  }

  function getProductFieldTitle() {
    return isGermanLocale()
      ? 'Dieser Wert wird automatisch anhand des aktuellen Produkts gesetzt'
      : 'This value is automatically set based on the current product';
  }

  // Helper function to get product title
  function getProductTitle() {
    // Method 1: Look for product title in meta tags or page elements
    const productTitleElements = [
      '.product__title',
      '.product-title',
      '.product_title',
      'h1.product__title',
      'h1[class*="product"]',
      '.product-meta h1',
      '.product-single__title'
    ];
    
    for (const selector of productTitleElements) {
      const element = document.querySelector(selector);
      if (element && element.textContent.trim()) {
        return element.textContent.trim();
      }
    }
    
    // Method 2: Check structured data
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of scripts) {
      try {
        const jsonData = JSON.parse(script.textContent);
        if (jsonData['@type'] === 'Product' && jsonData.name) {
          return jsonData.name;
        }
        if (Array.isArray(jsonData)) {
          for (const item of jsonData) {
            if (item['@type'] === 'Product' && item.name) {
              return item.name;
            }
          }
        }
      } catch (e) {
        continue;
      }
    }
    
    // Method 3: Check meta tags
    const metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle && metaTitle.content && metaTitle.content.toLowerCase() !== 'aldacom') {
      return metaTitle.content;
    }
    
    return null;
  }
  
  // Helper function to fill product title fields (backwards compatibility if any forms still use title)
  function fillProductTitleInForm(contactForm, productTitle) {
    const fieldSelectors = [
      // English
      'input[name*="product_title" i]',
      'input[name*="product-title" i]',
      'input[name*="productname" i]',
      'input[name*="product_name" i]',
      'input[name*="product-name" i]',
      'input[name*="item_title" i]',
      'input[name*="item-title" i]',
      'input[name*="itemname" i]',
      'input[name*="item-name" i]',
      // German
      'input[name*="produktname" i]',
      'input[name*="produkttitel" i]',
      // Textareas
      'textarea[name*="product_title" i]',
      'textarea[name*="product-title" i]',
      'textarea[name*="productname" i]',
      'textarea[name*="product_name" i]',
      'textarea[name*="product-name" i]',
      'textarea[name*="item_title" i]',
      'textarea[name*="item-title" i]',
      'textarea[name*="itemname" i]',
      'textarea[name*="item-name" i]',
      'textarea[name*="produktname" i]',
      'textarea[name*="produkttitel" i]'
    ];
    
    // Look for labeled fields
    const labeledFields = [];
    const labels = contactForm.querySelectorAll('label');
    labels.forEach(label => {
      const labelText = (label.textContent || '').toLowerCase().replace(/[:*]/g, '').trim();
      const matches = [
        // English
        'product title', 'product-name', 'product name', 'producttitle',
        'item title', 'item-name', 'item name',
        // German
        'produktname', 'produkt titel', 'produkt-titel', 'produkttitel',
        // French
        'nom du produit', 'nom produit',
        // Spanish
        'nombre del producto', 'titulo del producto', 'título del producto',
        // Italian
        'nome del prodotto', 'titolo del prodotto',
        // Portuguese
        'nome do produto', 'titulo do produto', 'título do produto',
        // Dutch
        'productnaam', 'product titel', 'producttitel'
      ];
      if (matches.some(m => labelText.includes(m))) {
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          const field = contactForm.querySelector('#' + forAttr);
          if (field && (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA')) {
            labeledFields.push(field);
          }
        }
      }
    });
    
    // Combine all possible fields
    const allFields = [
      ...contactForm.querySelectorAll(fieldSelectors.join(', ')),
      ...labeledFields
    ];
    
    // Fill the fields
    allFields.forEach(field => {
      if (field && !field.value.trim()) {
        field.value = productTitle;
        field.readOnly = true;
        field.style.backgroundColor = '#f8f9fa';
        field.style.cursor = 'default';
        field.title = getProductFieldTitle();
        field.classList.add('auto-filled-product-title');
        
        // Create hidden backup field
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = field.name;
        hiddenField.value = productTitle;
        hiddenField.classList.add('product-title-backup');
        field.parentNode.insertBefore(hiddenField, field);
        field.removeAttribute('name');
      }
    });
  }
  
  // Look for the collapsible contact form
  const contactForm = document.querySelector('.collapsible-contact-form');
  
  if (contactForm) {
    // Open the form if it's closed (details element) before calculating scroll position
    if (contactForm.tagName === 'DETAILS' && !contactForm.open) {
      contactForm.open = true;
    }
    
    // Alternative: look for the summary element and click it if the form is closed
    const summary = contactForm.querySelector('summary');
    if (summary && !contactForm.open) {
      summary.click();
    }

    // Helper to scroll
    const alignScroll = () => {
      contactForm.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    };

    // If found, scroll to it with smooth animation
    alignScroll();
    
    // Because lazy-loaded elements (like 'You may also like' recommendations) 
    // can shift the page height when they appear, we re-trigger the scroll 
    // after short delays to ensure the user lands exactly on the form.
    setTimeout(alignScroll, 400);
    setTimeout(alignScroll, 1000);
    
    // Focus on the first input field after a short delay
    setTimeout(() => {
      const firstInput = contactForm.querySelector('input[type="text"], input[type="email"], textarea');
      if (firstInput) {
        firstInput.focus();
      }
    }, 1200);
    
    // Auto-fill product title after opening the form
    setTimeout(() => {
      const productTitle = getProductTitle();
      if (productTitle) {
        fillProductTitleInForm(contactForm, productTitle);
      }
    }, 600);
    
  } else {
    // Fallback: try to find any contact form or section
    const fallbackSelectors = [
      '.contact-form',
      '[id*="contact"]',
      '.section-contact',
      'form[action*="contact"]'
    ];
    
    let foundElement = null;
    for (const selector of fallbackSelectors) {
      foundElement = document.querySelector(selector);
      if (foundElement) break;
    }
    
    if (foundElement) {
      foundElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Try to auto-fill in the found form too
      setTimeout(() => {
        const productTitle = getProductTitle();
        if (productTitle) {
          fillProductTitleInForm(foundElement, productTitle);
        }
      }, 600);
    } else {
      // If no contact form found, try to navigate to contact page
      console.log('No contact form found on this page');
    }
  }
}
